package top.wcpe.allincore.dsl.compiler.semantic.`native`.methods

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.semantic.`native`.Arity
import top.wcpe.allincore.dsl.compiler.semantic.`native`.HostClassNames
import top.wcpe.allincore.dsl.compiler.semantic.`native`.MethodDescriptor
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ResolveCtx

/**
 * 内置 object `net` 的 `request` 方法描述符。
 *
 * Stage 4：实现真实 lower 逻辑，把 MemberCall 转换为 NetRequestCall。
 * arity 按位置参数总数粗校验：ns + handler + payload + callback = 4。
 */
object NetRequestMethod : MethodDescriptor {

    override val name: String = HostClassNames.NetMethods.REQUEST

    override val arity: Arity = Arity.Range(3, 4)

    /**
     * 把 MemberCall(target=IdentRef("net"), method="request", args=[ns, handler, payload, callback?])
     * lower 为 ExprAst.NetRequestCall。
     *
     * Stage 5：支持 3 args（无 callback，await 上下文）或 4 args（有 callback，非 await 上下文）。
     * 返回 null 表示保留原 MemberCall（参数不符合预期时的防御行为）。
     */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? {
        // 防御：target 必须是 IdentRef("net")
        val target = call.target
        if (target !is ExprAst.IdentRef || target.name != HostClassNames.NET) return null

        // 位置参数 3 或 4 个：ns / handler / payload / [callback]
        if (call.args.size !in 3..4) return null

        val nsArg = call.args[0]
        val handlerArg = call.args[1]
        val payloadArg = call.args[2]

        // args[0] 必须是 StringLit
        if (nsArg !is ExprAst.Literal.StringLit) return null
        // args[1] 必须是 StringLit
        if (handlerArg !is ExprAst.Literal.StringLit) return null
        // args[2] 必须是 MapLiteralExpr
        if (payloadArg !is ExprAst.MapLiteralExpr) return null

        // args[3]（可选）必须是 LambdaExpr 或 FunRefExpr
        val callbackArg: ExprAst? = if (call.args.size == 4) {
            val cb = call.args[3]
            if (cb !is ExprAst.LambdaExpr && cb !is ExprAst.FunRefExpr) return null
            cb
        } else null

        // 从 namedArgs 中提取 timeoutMs
        val timeoutMs = call.namedArgs.firstOrNull { it.name == "timeoutMs" }?.value

        // 构造 payload entries
        val payload = payloadArg.entries.map { e ->
            ExprAst.NetPayloadEntry(key = e.key, value = e.value, position = e.position)
        }

        return ExprAst.NetRequestCall(
            namespace = nsArg.value,
            handler = handlerArg.value,
            payload = payload,
            callback = callbackArg,
            timeoutMs = timeoutMs,
            position = call.position,
        )
    }
}
